// Export of the matrix table to Excel and PDF. The libraries are loaded on demand so they only
// cost bytes when a button is actually used.

import { resolveWidgetAsset } from './assets.js'

// Match highlight colours mirror .cell--match in MatrixComponent.vue
const MATCH_BG = 'ECF4F5'
const MATCH_FG = '3E909F'
const HEADER_BG = 'F3F3F6'
const AXIS_BG = 'EDF0F1'
const TEXT = '00292E'

// model: { market, title, summary: string[], rowAxisLabel, colAxisLabel, colHeaders: string[],
//          rows: [{ header: string, cells: [{ text: string, match: boolean }] }] }

function fileName(model, ext) {
  const stamp = new Date().toISOString().slice(0, 10)
  const base = (model.title || 'matrix').toLowerCase().replace(/[^a-z0-9]+/g, '-')
  return `${base}-${stamp}.${ext}`
}

function download(blob, name) {
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = name
  document.body.appendChild(link)
  link.click()
  link.remove()
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}

// Logos live at images/logos/<market>.svg. Returns a PNG (both exporters need a raster image) or
// null when the market has no logo or anything along the way fails, so exports never break on it.
async function loadLogo(market) {
  if (!market) return null
  let objectUrl
  try {
    const res = await fetch(resolveWidgetAsset(`images/logos/${encodeURIComponent(market)}.svg`))
    if (!res.ok) return null
    const text = await res.text()
    // A static host may answer unknown paths with an HTML page and a 200
    if (!/^\s*(<\?xml[^>]*>\s*)?(<!--[\s\S]*?-->\s*)*<svg[\s>]/i.test(text)) return null

    const svg = new DOMParser().parseFromString(text, 'image/svg+xml').documentElement
    const [, , vbWidth, vbHeight] = (svg.getAttribute('viewBox') ?? '').split(/[\s,]+/).map(Number)
    if (!(vbWidth > 0 && vbHeight > 0)) return null
    // Firefox needs explicit dimensions to rasterize an SVG that only has a viewBox
    svg.setAttribute('width', String(vbWidth))
    svg.setAttribute('height', String(vbHeight))

    objectUrl = URL.createObjectURL(
      new Blob([new XMLSerializer().serializeToString(svg)], { type: 'image/svg+xml' })
    )
    const img = new Image()
    await new Promise((resolve, reject) => {
      img.onload = resolve
      img.onerror = reject
      img.src = objectUrl
    })

    const width = 1200
    const height = Math.round((width * vbHeight) / vbWidth)
    const canvas = document.createElement('canvas')
    canvas.width = width
    canvas.height = height
    canvas.getContext('2d').drawImage(img, 0, 0, width, height)
    const dataUrl = canvas.toDataURL('image/png')
    return { dataUrl, base64: dataUrl.split(',')[1], ratio: vbWidth / vbHeight }
  } catch {
    return null
  } finally {
    if (objectUrl) URL.revokeObjectURL(objectUrl)
  }
}

const solid = (argb) => ({ type: 'pattern', pattern: 'solid', fgColor: { argb: `FF${argb}` } })
const thin = { style: 'thin', color: { argb: 'FFD5DBDC' } }
const border = { top: thin, left: thin, bottom: thin, right: thin }

export async function exportMatrixToExcel(model) {
  const { default: ExcelJS } = await import('exceljs')
  const workbook = new ExcelJS.Workbook()
  const sheet = workbook.addWorksheet('Matrix')
  const colCount = model.colHeaders.length

  const logo = await loadLogo(model.market)
  if (logo) {
    const logoHeight = 28
    sheet.addRow([]).height = 36
    const imageId = workbook.addImage({ base64: logo.base64, extension: 'png' })
    sheet.addImage(imageId, {
      tl: { col: 0, row: 0.1 },
      ext: { width: Math.round(logoHeight * logo.ratio), height: logoHeight },
    })
  }

  for (const line of model.summary) sheet.addRow([line])

  const axisRow = sheet.addRow([model.rowAxisLabel, model.colAxisLabel])
  if (colCount > 1) sheet.mergeCells(axisRow.number, 2, axisRow.number, colCount + 1)
  axisRow.eachCell((cell) => {
    cell.font = { bold: true, color: { argb: `FF${TEXT}` } }
    cell.fill = solid(AXIS_BG)
    cell.border = border
  })

  const headerRow = sheet.addRow(['', ...model.colHeaders])
  headerRow.eachCell((cell) => {
    cell.font = { bold: true, color: { argb: `FF${TEXT}` } }
    cell.fill = solid(HEADER_BG)
    cell.alignment = { horizontal: 'center' }
    cell.border = border
  })

  for (const row of model.rows) {
    const excelRow = sheet.addRow([
      row.header,
      ...row.cells.map((c) => (c.value == null ? c.text : c.value)),
    ])
    excelRow.eachCell((cell, col) => {
      cell.border = border
      if (col === 1) {
        cell.font = { bold: true, color: { argb: `FF${TEXT}` } }
        cell.fill = solid(HEADER_BG)
        return
      }
      cell.alignment = { horizontal: 'center' }
      if (row.cells[col - 2].match) {
        cell.fill = solid(MATCH_BG)
        cell.font = { bold: true, color: { argb: `FF${MATCH_FG}` } }
        cell.border = {
          top: { style: 'thin', color: { argb: `FF${MATCH_FG}` } },
          left: { style: 'thin', color: { argb: `FF${MATCH_FG}` } },
          bottom: { style: 'thin', color: { argb: `FF${MATCH_FG}` } },
          right: { style: 'thin', color: { argb: `FF${MATCH_FG}` } },
        }
      }
    })
  }

  sheet.getColumn(1).width = 18
  for (let i = 2; i <= colCount + 1; i++) sheet.getColumn(i).width = 12

  const buffer = await workbook.xlsx.writeBuffer()
  download(
    new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    }),
    fileName(model, 'xlsx')
  )
}

const hexToRgb = (hex) => [0, 2, 4].map((i) => parseInt(hex.slice(i, i + 2), 16))

// Builds the PDF once so saving and printing produce exactly the same document
async function buildMatrixPdf(model) {
  const [{ jsPDF }, { default: autoTable }] = await Promise.all([
    import('jspdf'),
    import('jspdf-autotable'),
  ])
  const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' })
  const margin = 36

  const logo = await loadLogo(model.market)
  let top = margin
  if (logo) {
    const logoHeight = 20
    doc.addImage(logo.dataUrl, 'PNG', margin, margin, logoHeight * logo.ratio, logoHeight)
    top += logoHeight + 18
  }

  doc.setFont('helvetica', 'bold')
  doc.setFontSize(14)
  doc.text(model.title, margin, top)
  doc.setFont('helvetica', 'normal')
  doc.setFontSize(9)
  model.summary.forEach((line, i) => doc.text(line, margin, top + 16 + i * 12))

  const colCount = model.colHeaders.length
  autoTable(doc, {
    startY: top + 16 + model.summary.length * 12 + 8,
    margin,
    theme: 'grid',
    styles: {
      fontSize: 8,
      halign: 'center',
      textColor: hexToRgb(TEXT),
      lineColor: [213, 219, 220],
    },
    head: [
      [
        { content: model.rowAxisLabel, styles: { halign: 'left', fillColor: hexToRgb(AXIS_BG) } },
        {
          content: model.colAxisLabel,
          colSpan: colCount,
          styles: { halign: 'left', fillColor: hexToRgb(AXIS_BG) },
        },
      ],
      ['', ...model.colHeaders],
    ],
    headStyles: { fillColor: hexToRgb(HEADER_BG), textColor: hexToRgb(TEXT), fontStyle: 'bold' },
    body: model.rows.map((row) => [
      {
        content: row.header,
        styles: { halign: 'left', fontStyle: 'bold', fillColor: hexToRgb(HEADER_BG) },
      },
      ...row.cells.map((c) =>
        c.match
          ? {
              content: c.text,
              styles: {
                fillColor: hexToRgb(MATCH_BG),
                textColor: hexToRgb(MATCH_FG),
                fontStyle: 'bold',
              },
            }
          : c.text
      ),
    ]),
  })

  return doc
}

export async function exportMatrixToPdf(model) {
  const doc = await buildMatrixPdf(model)
  doc.save(fileName(model, 'pdf'))
}

// Opens the print dialog for the same PDF the export produces, via a hidden frame so the page
// the widget sits on is left alone
export async function printMatrix(model) {
  const doc = await buildMatrixPdf(model)
  const url = URL.createObjectURL(doc.output('blob'))
  const frame = document.createElement('iframe')
  frame.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0'
  frame.src = url
  frame.onload = () => {
    frame.contentWindow.focus()
    frame.contentWindow.print()
  }
  document.body.appendChild(frame)
  setTimeout(() => {
    frame.remove()
    URL.revokeObjectURL(url)
  }, 120000)
}
